import Button from '@/components/Button';
import { classNames } from '@/utils/class-names';
import DateRangePicker from '../../../../components/DateRangePicker';
import { useNavigate } from 'react-router-dom';
import { useAdminStore } from '../../store/useAdmin.store';
import { useEffect, useState } from 'react';
import { Dayjs } from 'dayjs';
import { updateSchoolTermDates } from '@/services/global/admin.service';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useToast } from '@/components/ui/use-toast';

export const OnBoardingAdminStep2 = () => {
  const { page, onInit } = useAdminStore();
  const { authentication } = useAuthentication();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!page) onInit();
  }, [onInit, page]);

  const [autumnTermStartDate, setAutumnTermStartDate] = useState<Dayjs | null>(null);
  const [autumnTermEndDate, setAutumnTermEndDate] = useState<Dayjs | null>(null);
  const [springTermStartDate, setSpringTermStartDate] = useState<Dayjs | null>(null);
  const [springTermEndDate, setSpringTermEndDate] = useState<Dayjs | null>(null);
  const [summerTermStartDate, setSummerTermStartDate] = useState<Dayjs | null>(null);
  const [summerTermEndDate, setSummerTermEndDate] = useState<Dayjs | null>(null);

  const title = page?.step2.title || '';
  const subTitle = page?.step2.subTitle || '';
  const btnText = page?.step2.btnText || '';
  const autumTermTitle = page?.step2.autumTermTitle || '';
  const springTerm = page?.step2.springTerm || '';
  const summerTerm = page?.step2.summerTerm || '';

  const isNextDisabled =
    !autumnTermStartDate ||
    !autumnTermEndDate ||
    !springTermStartDate ||
    !springTermEndDate ||
    !summerTermStartDate ||
    !summerTermEndDate;

  if (title === '') {
    return null;
  }

  const onSubmit = async () => {
    const data = {
      autumnTermStartDate: autumnTermStartDate ? autumnTermStartDate.format('YYYY-MM-DD') : null,
      autumnTermEndDate: autumnTermEndDate ? autumnTermEndDate.format('YYYY-MM-DD') : null,
      springTermStartDate: springTermStartDate ? springTermStartDate.format('YYYY-MM-DD') : null,
      springTermEndDate: springTermEndDate ? springTermEndDate.format('YYYY-MM-DD') : null,
      summerTermStartDate: summerTermStartDate ? summerTermStartDate.format('YYYY-MM-DD') : null,
      summerTermEndDate: summerTermEndDate ? summerTermEndDate.format('YYYY-MM-DD') : null,
    };

    const { error } = await updateSchoolTermDates(String(authentication.user.school.id), data);
    if (error) {
      toast({
        title: 'Error updating school term dates',
        description: error,
        variant: 'destructive',
      });
      return;
    }
    navigate('/school-management');
  };

  return (
    <form onSubmit={onSubmit} className="mt-sh-60 flex flex-col items-center justify-center px-4">
      <div className="flex gap-[10px]">
        {[1, 2, 3].map((_, index) => (
          <div
            className={classNames(
              'flex size-[40px] items-center justify-center rounded-full border border-primary-500',
              index !== 1 ? 'opacity-50' : 'bg-white',
            )}
            key={index}>
            <span className="text-sh-xxxs font-bold">{index + 1}</span>
          </div>
        ))}
      </div>

      <div className="mt-[80px] flex w-full flex-col items-center justify-center gap-sh-30">
        <h1 className="text-sh-md font-bold">{title}</h1>
        <h2 className="text-sh-sm font-bold">{subTitle}</h2>
        <DateRangePicker
          label={autumTermTitle}
          startDate={autumnTermStartDate}
          endDate={autumnTermEndDate}
          onStartDateChange={setAutumnTermStartDate}
          onEndDateChange={setAutumnTermEndDate}
        />
        <DateRangePicker
          label={springTerm}
          startDate={springTermStartDate}
          endDate={springTermEndDate}
          onStartDateChange={setSpringTermStartDate}
          onEndDateChange={setSpringTermEndDate}
        />
        <DateRangePicker
          label={summerTerm}
          startDate={summerTermStartDate}
          endDate={summerTermEndDate}
          onStartDateChange={setSummerTermStartDate}
          onEndDateChange={setSummerTermEndDate}
        />
      </div>

      <div className="mt-20 flex flex-col items-center">
        <Button
          className="mb-28"
          onClick={() => {
            onSubmit();
          }}
          disabled={isNextDisabled}>
          {btnText}
        </Button>
      </div>
    </form>
  );
};
